<template>
    <form
        v-if="!signed && !signing && !submitted && !submitting"
        @submit.prevent="onSubmit"
        enctype="multipart/form-data"
    >
        <h4>{{ introHeader }}</h4>
        <p class="preserve-linebrakes mb-5">{{ introBody }}</p>

        <div
            v-for="(answer, answerIdx) in answers"
            :key="answer.question.uuid"
            class="mb-5"
        >
            <h6 class="font-weight-bold">
                {{ answer.question.prompt }}
                <template v-if="!answer.question.optional">*</template>
            </h6>

            <template v-if="answer.question.type === 'text'">
                <input
                    type="text"
                    class="form-control"
                    :required="!answer.question.optional"
                    v-model="answer.value"
                />
            </template>

            <template v-if="answer.question.type === 'textarea'">
                <textarea
                    class="form-control"
                    :required="!answer.question.optional"
                    rows="5"
                    v-model="answer.value"
                ></textarea>
            </template>

            <template
                v-if="
                    answer.question.type === 'checkbox' ||
                    answer.question.type === 'radio'
                "
            >
                <div
                    v-for="(option, optionIdx) in answer.question.options"
                    :key="optionIdx"
                >
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            :type="answer.question.type"
                            :required="
                                !answer.question.optional &&
                                JSON.stringify(answer.value) === '[]'
                            "
                            :id="`${uuid}—question-${answer.question.uuid}-option-${optionIdx}`"
                            :name="`${uuid}—question-${answer.question.uuid}`"
                            :value="option"
                            v-model="answer.value"
                        />
                        <label
                            class="form-check-label"
                            :for="`${uuid}—question-${answer.question.uuid}-option-${optionIdx}`"
                        >
                            {{ option }}
                        </label>
                    </div>
                </div>
            </template>
            <template v-if="answer.question.type === 'documents'">
                <input
                    type="file"
                    :required="!answer.question.optional"
                    :name="`${uuid}—question-${answer.question.uuid}`"
                    v-on:change="
                        (event) => handleFiles(answerIdx, event.target.files)
                    "
                    multiple
                />
            </template>
            <template
                v-if="
                    answer.question.type === 'products-checkbox' ||
                    answer.question.type === 'products-radio'
                "
            >
                <div
                    v-for="(product, productIdx) in answer.question.products"
                    :key="productIdx"
                >
                    <div class="product">
                        <input
                            :type="
                                answer.question.type.replace('products-', '')
                            "
                            :required="
                                !answer.question.optional &&
                                JSON.stringify(answer.value) === '[]'
                            "
                            :id="`${uuid}—question-${answer.question.uuid}-product-${productIdx}`"
                            :name="`${uuid}—question-${answer.question.uuid}`"
                            :value="product"
                            v-model="answer.value"
                        />
                        <label
                            :for="`${uuid}—question-${answer.question.uuid}-product-${productIdx}`"
                        >
                            <img :src="product.image_url" />
                            <div class="product-info">
                                <strong>{{ product.name }}</strong
                                ><br />
                                {{
                                    $t(
                                        "products.unit_with_price." +
                                            product.price_unit,
                                        {
                                            price: product.price_including_vat,
                                            currency: $t(
                                                "currencies." + product.currency
                                            ),
                                            vat_amount: "",
                                        }
                                    )
                                }}<br />
                                <h6
                                    v-if="product.description_title"
                                    class="font-weight-bold"
                                >
                                    {{ product.description_title }}
                                </h6>
                                <p
                                    v-if="product.description"
                                    v-html="product.description"
                                ></p>
                            </div>
                        </label>
                    </div>
                </div>
            </template>
        </div>

        <div v-if="!submitting && !signing" class="text-center">
            <SbButton type="submit" variant="custom" class="signbuttoninline">
                <template v-if="requiresBankidSignature && !signed">
                    Signera med BankID
                </template>
                <template v-else> Skicka in </template>
            </SbButton>
        </div>
    </form>

    <div v-else-if="signing" class="text-center my-5">
        <bank-id-v2-sign
            :item-id="bankidItemId"
            :item-description="`Jag signerar mina svar på förfrågan ${introHeader}`"
            :app-url="bankidAppUrl"
            :quote-key="quoteKey"
            :signee-name="bankidSigneeName"
            autostart
            @signed="onSigned"
        ></bank-id-v2-sign>
    </div>
    <div v-else-if="submitting" class="text-center my-5">
        <SbSpinner></SbSpinner>
    </div>
    <div v-else-if="submitted">
        <h5>{{ thanksHeader }}</h5>
        <p class="preserve-linebrakes">{{ thanksBody }}</p>

        <div class="text-center my-5">
            <SbButton
                v-if="buttonNextEnabled"
                type="button"
                variant="custom"
                class="signbuttoninline"
                @click="emit('next')"
            >
                Gå vidare till nästa enkät
            </SbButton>

            <SbButton
                v-else
                type="button"
                variant="custom"
                class="signbuttoninline"
                @click="emit('close')"
            >
                Stäng
            </SbButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
p.preserve-linebrakes {
    white-space: pre-line;
}

.product {
    display: flex;
    align-items: flex-start;

    input {
        margin-right: 8px;
        margin-top: 30px;
        flex-shrink: 0;
        flex-grow: 0;
    }

    label {
        flex-grow: 1;
        display: flex;
        align-items: flex-start;

        img {
            flex-shrink: 0;
            flex-grow: 0;
            height: 70px;
            width: 70px;
            object-fit: contain;
            object-position: center;
            margin-right: 8px;
        }

        img[src=""],
        img:not([src]) {
            opacity: 0;
        }

        .product-info {
            padding-top: 4px;
        }
    }
}
</style>

<script setup lang="ts">
import axios, { AxiosError } from "axios";
import { computed, ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import SbButton from "../sb/SbButton.vue";
import SbSpinner from "../sb/SbSpinner.vue";

type Question = Record<string, unknown>;
type Answer = { question: Question; value: unknown[] };
interface Props {
    imageUrl?: string | null; requiresBankidSignature: boolean; bankidItemId?: string | null;
    bankidAppUrl?: string | null; bankidSigneeName?: string | null; introHeader: string;
    introBody: string; thanksHeader: string; thanksBody: string; questions: Question[];
    quoteKey: string; buttonNextEnabled?: boolean; submitUrl?: string | null;
}
const props = withDefaults(defineProps<Props>(), {
    imageUrl: null, bankidItemId: null, bankidAppUrl: null, bankidSigneeName: null, submitUrl: null,
});
const emit = defineEmits<{ (event: "next" | "close"): void }>();
const answers = ref<Answer[]>(props.questions.map((question) => ({ question: { ...question }, value: [] })));
const submitted = ref(false);
const submitting = ref(false);
const signed = ref(false);
const signing = ref(false);
const uuid = uuidv4();
const preview = computed(() => !props.submitUrl);
function handleFiles(answerIdx: number, files?: FileList | File[]): void {
    if (files?.length && answers.value[answerIdx]) answers.value[answerIdx].value = Array.from(files);
}
async function onSigned(): Promise<void> { signing.value = false; signed.value = true; await onSubmit(); }
async function onSubmit(): Promise<void> {
    if (props.requiresBankidSignature && !signed.value) { signing.value = true; return; }
    submitting.value = true;
    if (preview.value) {
        window.setTimeout(() => { submitted.value = true; submitting.value = false; }, 1000);
        return;
    }
    try {
        await axios.postForm(props.submitUrl as string, { _method: "PUT", answers: answers.value, completed: true });
        submitted.value = true;
    } catch (error) {
        console.error(error);
        alert((error as AxiosError).response?.status === 422 ? "Ogiltig filtyp eller storlek" : "Något gick fel, försök igen senare.");
    } finally {
        submitting.value = false;
    }
}
</script>
